"use client"

import { useState, useMemo } from "react"
import { Bell } from "lucide-react"
import { SchoolSidebar } from "../components/ui/school/school-dashboard/school-sidebar"
import { StudentsFilters } from "../components/ui/school/students-management/students-filters"
import { StudentsTable } from "../components/ui/school/students-management/students-table"
import { PaginationStats } from "../components/ui/school/students-management/pagination-stats"

// Mock data for students
const mockStudents = [
  {
    id: "1",
    name: "Adewale Johnson",
    grade: "SS 3",
    classSection: "Science A",
    progressPercentage: 85,
    careerPath: "Engineering",
  },
  {
    id: "2",
    name: "Chioma Okafor",
    grade: "SS 2",
    classSection: "Arts B",
    progressPercentage: 72,
    careerPath: "Medicine",
  },
  {
    id: "3",
    name: "Tunde Balogun",
    grade: "SS 1",
    classSection: "Commercial A",
    progressPercentage: 90,
    careerPath: "Business",
  },
  {
    id: "4",
    name: "Amina Yusuf",
    grade: "JSS 3",
    classSection: "Basic 3A",
    progressPercentage: 67,
    careerPath: "Technology",
  },
  {
    id: "5",
    name: "Emmanuel Okoro",
    grade: "SS 3",
    classSection: "Science B",
    progressPercentage: 88,
    careerPath: "Engineering",
  },
  {
    id: "6",
    name: "Blessing Adeleke",
    grade: "SS 2",
    classSection: "Science A",
    progressPercentage: 78,
    careerPath: "Medicine",
  },
  {
    id: "7",
    name: "Ibrahim Mohammed",
    grade: "SS 1",
    classSection: "Arts A",
    progressPercentage: 81,
    careerPath: "Arts",
  },
  {
    id: "8",
    name: "Funke Ajayi",
    grade: "JSS 2",
    classSection: "Basic 2B",
    progressPercentage: 74,
    careerPath: "Science",
  },
  {
    id: "9",
    name: "David Nwosu",
    grade: "SS 3",
    classSection: "Commercial B",
    progressPercentage: 92,
    careerPath: "Business",
  },
  {
    id: "10",
    name: "Sarah Ogunleye",
    grade: "SS 2",
    classSection: "Arts B",
    progressPercentage: 69,
    careerPath: "Education",
  },
]

export default function StudentsManagementPage() {
  const [filters, setFilters] = useState({
    searchQuery: "",
    grade: "",
    careerPath: "",
  })
  const [currentPage, setCurrentPage] = useState(1)
  const resultsPerPage = 8

  // Filter students based on search and filters
  const filteredStudents = useMemo(() => {
    return mockStudents.filter((student) => {
      const matchesSearch =
        filters.searchQuery === "" || student.name.toLowerCase().includes(filters.searchQuery.toLowerCase())
      const matchesGrade = filters.grade === "" || student.grade === filters.grade
      const matchesCareerPath = filters.careerPath === "" || student.careerPath === filters.careerPath

      return matchesSearch && matchesGrade && matchesCareerPath
    })
  }, [filters])

  // Calculate pagination
  const totalPages = Math.ceil(filteredStudents.length / resultsPerPage)
  const paginatedStudents = filteredStudents.slice((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage)

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    setCurrentPage(1) // Reset to first page when filters change
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* School Sidebar */}
      <SchoolSidebar />

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 p-4 md:p-8 overflow-y-auto">
        {/* Header */}
        <header className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Students Management</h1>
            <p className="text-gray-500 mt-1">View and manage all student records</p>
          </div>
          <button className="relative p-3 rounded-full hover:bg-white transition border border-gray-200">
            <Bell className="text-gray-600 w-6 h-6" />
            <span className="absolute top-2 right-2 block h-2 w-2 rounded-full bg-red-500"></span>
          </button>
        </header>

        {/* Filters Bar */}
        <StudentsFilters onFilterChange={handleFilterChange} />

        {/* Students Table */}
        <StudentsTable students={paginatedStudents} />

        {/* Pagination Stats */}
        {filteredStudents.length > 0 && (
          <PaginationStats
            currentPage={currentPage}
            totalPages={totalPages}
            totalResults={filteredStudents.length}
            resultsPerPage={resultsPerPage}
            onPageChange={setCurrentPage}
          />
        )}
      </main>
    </div>
  )
}